<template>
  <div class="product-carousel" :class="classes">
    <div v-if="title" class="product-carousel__header container">
      <h2>{{ title }}</h2>
      <Button
        v-if="headerLink"
        tag="a"
        :href="headerLink.link"
        transparent
        class="shop-all-link"
      >
        {{ headerLink.text }}
      </Button>
    </div>
    <div class="photos__wrapper">
      <div ref="photos" class="photos">
        <PhotoTile
          v-for="product in products"
          :key="product.id"
          :product="product"
          :flat-lays="flatLays"
        />
      </div>

      <button class="control control--prev">
        <span v-if="!hidePrev" class="icon icon--prev" @click="previous">
          <svg viewBox="0 0 14 25">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12.647 12.661L.824.838l.354-.354 12.176 12.177L1.178 24.838l-.354-.354 11.823-11.823z"
            ></path>
          </svg>
        </span>
      </button>
      <button class="control control--next">
        <span v-if="!hideNext" class="icon" @click="next">
          <svg viewBox="0 0 14 25">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12.647 12.661L.824.838l.354-.354 12.176 12.177L1.178 24.838l-.354-.354 11.823-11.823z"
            ></path>
          </svg>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import Button from "@/components/Button.vue";
import PhotoTile from "@/components/PhotoTile.vue";

export default {
  name: "ProductCarouselV2",
  components: {
    PhotoTile,
    Button,
  },
  props: {
    products: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      default: null,
    },
    headerLink: {
      type: Object,
      default: null,
    },
    flatLays: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      observer: null,
      indexOfVisible: 0,
      highestIndexVisible: 0,
      hidePrev: true,
      hideNext: false,
      intersectingEntries: [],
      lastEntry: null,
    };
  },
  computed: {
    items() {
      return this.$refs.photos.children;
    },
    classes() {
      return {
        "product-carousel--with-header": !!this.title,
      };
    },
  },
  mounted() {
    this.observer = new IntersectionObserver(
      (entries) => {
        [...entries].forEach((entry) => {
          if (entry.isIntersecting) {
            this.intersectingEntries.push(entry);
            entry.target.dataset.intersected = true;
          } else {
            const spliceIndex = this.intersectingEntries.findIndex((el) =>
              el.target.isSameNode(entry.target)
            );

            if (spliceIndex >= 0) {
              this.intersectingEntries.splice(spliceIndex, 1);
            }

            entry.target.dataset.intersected = false;
          }
        });

        Array.prototype.leftMostElement = function () {
          return (
            (this.length &&
              this.reduce(function (prev, curr) {
                return prev.target.getBoundingClientRect().x <
                  curr.target.getBoundingClientRect().x
                  ? prev
                  : curr;
              })) ||
            null
          );
        };

        Array.prototype.leftMostElement = function () {
          return (
            (this.length &&
              this.reduce(function (prev, curr) {
                return prev.target.getBoundingClientRect().x <
                  curr.target.getBoundingClientRect().x
                  ? prev
                  : curr;
              })) ||
            null
          );
        };

        const leftMostInsectedEntry =
          this.intersectingEntries.leftMostElement();

        const target = leftMostInsectedEntry?.target;

        const index =
          [...this.items].findIndex((el) => el.isSameNode(target)) || 0;

        this.indexOfVisible = index;

        const lastItem = [...this.$refs["photos"].children].at(-1);
        const lastItemIntersected = Boolean(
          lastItem.dataset.intersected === "true"
        );

        if (lastItemIntersected) {
          this.hideNext = true;
        } else {
          this.hideNext = false;
        }

        if (this.indexOfVisible === 0) {
          this.hidePrev = true;
        } else {
          this.hidePrev = false;
        }
      },
      {
        root: this.$refs.photos,
        threshold: 0.9,
      }
    );

    for (const element of this.items) {
      this.observer.observe(element);
    }
  },
  unmounted() {
    this.observer.disconnect();
  },
  methods: {
    previous() {
      if (this.indexOfVisible === 0) {
        return;
      }

      this.items[this.indexOfVisible - 1].scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "start",
      });
    },
    next() {
      if (this.indexOfVisible === this.items.length - 1) {
        return;
      }

      this.items[this.indexOfVisible + 1].scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "start",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.product-carousel {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 25px;
    padding-bottom: 25px;
  }

  .shop-all-link {
    display: none;

    @include respond-above(md) {
      display: inline-block;
    }
  }

  h2 {
    font-weight: 500;
    text-transform: uppercase;
    font-size: 16px;
    line-height: 1;

    @include respond-above(md) {
      font-size: 30px;
    }
  }

  &--with-header {
    padding-bottom: 10px;
  }
}

.photos__wrapper {
  position: relative;
}

.photos {
  display: flex;
  overflow-y: hidden;
  flex-flow: row nowrap;
  scroll-snap-type: x mandatory;
  gap: 16px;

  &::-webkit-scrollbar {
    display: none;
  }

  & > * {
    scroll-snap-align: start;
    scroll-snap-stop: always;
    flex: 1 0 80%;

    @include respond-above(md) {
      flex: 1 0 348px;
    }
  }
}

.control {
  transition: 0.2s ease-in-out;
  appearance: none;
  background: 0;
  border: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  &--prev {
    left: 10px;
  }

  &--next {
    right: 10px;
  }

  .theme--light & {
    filter: invert(1);
  }
}

.icon {
  display: inline-block;

  &--prev {
    transform: rotateY(180deg);
  }

  svg {
    display: block;
    fill: rgb(255, 255, 255);
    height: 24px;
    width: 12px;
  }
}
</style>
